import type { PublicApartment } from '@/api/generated';

// Per-page <head> management for SEO. Build-time prerender + sitemap are a
// later cross-cutting slice (ADR-028); for now we inject JSON-LD + OG/meta
// tags at runtime so in-app navigation and crawlers that execute JS see
// per-page metadata. Helmet-free: a tiny imperative DOM updater keyed by a
// data attribute so repeated calls replace rather than duplicate.

const MANAGED_ATTR = 'data-seo-managed';

function clearManaged(): void {
  document.head
    .querySelectorAll(`[${MANAGED_ATTR}]`)
    .forEach((el) => el.remove());
}

function setTitle(title: string): void {
  document.title = title;
}

// addMeta upserts a meta tag: if one already exists for the same name/property
// (e.g. the static defaults in index.html), update it in place instead of
// appending a duplicate — otherwise crawlers and og: scrapers read the stale
// first tag. The selector key is the name= or property= attribute.
function addMeta(attrs: Record<string, string>): void {
  const key = attrs.property
    ? `meta[property="${attrs.property}"]`
    : attrs.name
      ? `meta[name="${attrs.name}"]`
      : null;
  const el =
    (key && document.head.querySelector<HTMLMetaElement>(key)) ||
    document.createElement('meta');
  el.setAttribute(MANAGED_ATTR, '');
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  if (!el.parentNode) document.head.appendChild(el);
}

function addJsonLd(data: unknown): void {
  const el = document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute(MANAGED_ATTR, '');
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

// addLink upserts a <link> tag keyed by rel (+ optional href-dedupe).
// Same MANAGED_ATTR pattern as addMeta: removes old managed link with the same
// rel before appending so SPA navigation never accumulates duplicates.
function addLink(attrs: Record<string, string>): void {
  const rel = attrs.rel;
  if (rel) {
    document.head
      .querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"][${MANAGED_ATTR}]`)
      .forEach((el) => el.remove());
  }
  const el = document.createElement('link');
  el.setAttribute(MANAGED_ATTR, '');
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  document.head.appendChild(el);
}

export interface PageSeo {
  title: string;
  description?: string;
  image?: string;
  /** Canonical URL for this page. When omitted, no canonical is emitted. */
  url?: string;
  jsonLd?: unknown;
  /**
   * When true: emits <meta name="robots" content="noindex"> and suppresses
   * the canonical link. Use for token pages, checkout, cabinet, login.
   */
  noindex?: boolean;
}

// applySeo replaces all previously-managed head tags with the given page's
// title, description, Open Graph tags, optional JSON-LD, canonical link, and
// robots noindex. Returns a cleanup function suitable for a useEffect teardown.
export function applySeo(seo: PageSeo): () => void {
  clearManaged();
  setTitle(seo.title);
  addMeta({ name: 'description', content: seo.description ?? '' });
  addMeta({ property: 'og:title', content: seo.title });
  addMeta({ property: 'og:type', content: 'website' });
  if (seo.description) {
    addMeta({ property: 'og:description', content: seo.description });
  }
  if (seo.image) {
    addMeta({ property: 'og:image', content: seo.image });
    // index.html declares 1200x630 for the default card (OH-58). Repointing
    // og:image leaves those numbers describing a different image, and the
    // client cannot know this one's size — so drop them instead of lying.
    // Scrapers never reach this code (they do not run JS); what they see is
    // the edge Function, which sets image and dimensions together.
    document.head
      .querySelectorAll(
        'meta[property="og:image:width"], meta[property="og:image:height"]',
      )
      .forEach((el) => el.remove());
  }
  if (seo.url) addMeta({ property: 'og:url', content: seo.url });
  if (seo.jsonLd) addJsonLd(seo.jsonLd);

  if (seo.noindex) {
    // Mark private/transactional pages as noindex so they are never indexed
    // even if a crawler executes JS. These pages are also Disallowed in
    // robots.txt; the double belt-and-suspenders is intentional.
    addMeta({ name: 'robots', content: 'noindex' });
  } else if (seo.url) {
    // Derive canonical from the URL's pathname only (strips query/hash so a
    // secret token in /booking/$token never surfaces in a canonical tag).
    try {
      const canonical = new URL(seo.url).origin + new URL(seo.url).pathname;
      addLink({ rel: 'canonical', href: canonical });
    } catch {
      // url is not a valid absolute URL (e.g. in SSR/test contexts) — skip.
    }
  }

  return clearManaged;
}

// apartmentJsonLd builds a schema.org Apartment node for an apartment detail
// page. schema.org/Apartment is the most specific type for a rentable unit;
// amenityFeature + numberOfRooms + floorSize are the fields Google Vacation
// Rentals / rich results understand.
export function apartmentJsonLd(
  apt: PublicApartment,
  pageUrl: string,
): Record<string, unknown> {
  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Apartment',
    name: apt.title,
    url: pageUrl,
  };
  if (apt.description) node.description = apt.description;
  if (apt.photos.length > 0) node.image = apt.photos.map((p) => p.url);
  if (typeof apt.bedrooms === 'number') node.numberOfBedrooms = apt.bedrooms;
  if (typeof apt.occupancy_max === 'number') {
    node.occupancy = {
      '@type': 'QuantitativeValue',
      maxValue: apt.occupancy_max,
      unitText: 'person',
    };
  }
  if (typeof apt.area_sqm === 'number') {
    node.floorSize = {
      '@type': 'QuantitativeValue',
      value: apt.area_sqm,
      unitCode: 'MTK',
    };
  }
  if (apt.city) {
    node.address = { '@type': 'PostalAddress', addressLocality: apt.city };
  }
  if (apt.amenities.length > 0) {
    node.amenityFeature = apt.amenities.map((a) => ({
      '@type': 'LocationFeatureSpecification',
      name: a,
      value: true,
    }));
  }
  return node;
}
